<template>
  <div class="stock-watch">
    <header id="blog-header">
      <div class="inner">
        <h1 class="page-title">股票监控</h1>
        <span class="page-desc">自选股票行情与估值(美股 · A股 · 港股)</span>
      </div>
    </header>

    <div class="stock-content">
      <div class="stock-toolbar">
        <button @click="loadStocks" :disabled="loading">{{ loading ? '刷新中...' : '刷新' }}</button>
        <a class="manage-link" href="/admin/stock_record/optionticker/">后台管理监控清单</a>
        <span class="model-name" v-if="modelName">评估模型: {{ modelName }}</span>
      </div>

      <div v-if="loading" class="loading">加载中...</div>
      <div v-if="error" class="error-msg">{{ error }}</div>
      <div v-if="!loading && stocks.length === 0" class="empty-hint">
        暂无自选股票。请在 <a href="/admin/stock_record/optionticker/">后台监控清单</a> 勾选股票后，
        执行每日分析任务或等待定时任务同步。
      </div>

      <template v-if="stocks.length > 0">
        <div class="stats-row">
          <div class="stat-item">
            <span>全部</span>
            <strong>{{ stocks.length }}</strong>
          </div>
          <div class="stat-item">
            <span>当前筛选</span>
            <strong>{{ filteredStocks.length }}</strong>
          </div>
          <div class="stat-item">
            <span>美股</span>
            <strong>{{ marketCount('us') }}</strong>
          </div>
          <div class="stat-item">
            <span>A股</span>
            <strong>{{ marketCount('ashare') }}</strong>
          </div>
          <div class="stat-item">
            <span>港股</span>
            <strong>{{ marketCount('hk') }}</strong>
          </div>
        </div>

        <div class="filter-panel">
          <label class="filter-field search-field">
            <span>搜索</span>
            <input v-model.trim="searchKeyword" type="search" placeholder="代码或名称">
          </label>
          <label class="filter-field">
            <span>市场</span>
            <select v-model="marketFilter">
              <option value="">全部市场</option>
              <option value="us">美股</option>
              <option value="ashare">A股</option>
              <option value="hk">港股</option>
            </select>
          </label>
          <label class="filter-field">
            <span>结论</span>
            <select v-model="verdictFilter">
              <option value="">全部结论</option>
              <option value="undervalued">低估</option>
              <option value="neutral">中性</option>
              <option value="overvalued">高估</option>
            </select>
          </label>
          <label class="filter-field">
            <span>估值日期</span>
            <select v-model="dateFilter">
              <option value="">全部日期</option>
              <option v-for="d in stockDates" :key="d" :value="d">{{ d }}</option>
            </select>
          </label>
          <label class="filter-field">
            <span>排序</span>
            <select v-model="sortKey">
              <option value="symbol">代码</option>
              <option value="name">名称</option>
              <option value="market">市场</option>
              <option value="change_pct">日涨跌</option>
              <option value="percentile_52w">52周分位</option>
              <option value="composite">综合分</option>
              <option value="date">估值日期</option>
            </select>
          </label>
          <button class="sort-dir" @click="sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'">
            {{ sortDirection === 'asc' ? '升序' : '降序' }}
          </button>
          <button class="reset-btn" @click="resetFilters">重置</button>
        </div>

        <p class="model-hint">
          点击行查看完整模型评估过程。期权分析仅美股支持，A股/港股用于行情和估值监控。
        </p>

        <div v-if="filteredStocks.length === 0" class="empty-hint">
          当前筛选条件下没有匹配的股票。
        </div>

        <div class="table-wrapper" v-else>
          <table class="stock-table">
            <thead>
              <tr>
                <th><button class="sort-button" @click="sortBy('symbol')">代码 {{ sortIndicator('symbol') }}</button></th>
                <th><button class="sort-button" @click="sortBy('name')">名称 {{ sortIndicator('name') }}</button></th>
                <th><button class="sort-button" @click="sortBy('market')">市场 {{ sortIndicator('market') }}</button></th>
                <th>收盘</th>
                <th><button class="sort-button" @click="sortBy('change_pct')">日涨跌 {{ sortIndicator('change_pct') }}</button></th>
                <th>行情日期</th>
                <th><button class="sort-button" @click="sortBy('percentile_52w')">52周分位 {{ sortIndicator('percentile_52w') }}</button></th>
                <th><button class="sort-button" @click="sortBy('composite')">综合分 {{ sortIndicator('composite') }}</button></th>
                <th>结论</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="v in filteredStocks">
                <tr :key="v.symbol" class="stock-row" @click="toggleExpand(v.symbol)">
                  <td class="ticker-cell">{{ v.symbol }}</td>
                  <td>{{ v.name || '-' }}</td>
                  <td>{{ marketOf(v.symbol) }}</td>
                  <td>{{ formatPrice(v.close, 2) }}</td>
                  <td :class="changeClass(v.change_pct)">{{ formatSignedPercent(v.change_pct) }}</td>
                  <td>{{ v.quote_date || '-' }}</td>
                  <td>{{ formatPercent(v.percentile_52w, 0, false) }}</td>
                  <td :class="compositeClass(v)">{{ compositeOf(v) }}</td>
                  <td>
                    <span :class="'verdict-' + v.verdict">{{ verdictLabel(v.verdict) }}</span>
                    <span v-if="v.detail && v.detail.confidence && v.detail.confidence !== '无'" class="confidence">
                      置信{{ v.detail.confidence }}
                    </span>
                  </td>
                </tr>
                <tr v-if="expandedSymbol === v.symbol" :key="v.symbol + '-detail'" class="detail-row">
                  <td colspan="9">
                    <div class="eval-process">
                      <div class="detail-heading">
                        <h4>{{ v.name || v.symbol }} ({{ v.symbol }})</h4>
                        <span>估值日期 {{ v.date || '-' }}</span>
                      </div>
                      <div class="detail-grid">
                        <div><span>收盘</span><strong>{{ formatPrice(v.close, 2) }}</strong></div>
                        <div><span>前收</span><strong>{{ formatPrice(v.prev_close, 2) }}</strong></div>
                        <div><span>日涨跌</span><strong :class="changeClass(v.change_pct)">{{ formatSignedPercent(v.change_pct) }}</strong></div>
                        <div><span>52周分位</span><strong>{{ formatPercent(v.percentile_52w, 0, false) }}</strong></div>
                        <div><span>比值</span><strong>{{ formatPrice(v.ratio, 4) }}</strong></div>
                        <div><span>比值Z分</span><strong>{{ formatPrice(v.ratio_z_score, 2) }}</strong></div>
                        <div><span>1年Z分</span><strong>{{ formatPrice(v.z_score_1y, 2) }}</strong></div>
                        <div><span>2年Z分</span><strong>{{ formatPrice(v.z_score_2y, 2) }}</strong></div>
                        <div><span>5年Z分</span><strong>{{ formatPrice(v.z_score_5y, 2) }}</strong></div>
                      </div>
                      <div class="history-section">
                        <div class="section-heading">
                          <h5>历史行情(近一年)</h5>
                          <span v-if="quoteState(v.symbol).source">来源 {{ quoteSourceLabel(quoteState(v.symbol).source) }}</span>
                        </div>
                        <div v-if="quoteState(v.symbol).loading" class="chart-message">历史行情加载中...</div>
                        <div v-else-if="quoteState(v.symbol).error" class="chart-message error-msg">{{ quoteState(v.symbol).error }}</div>
                        <div v-else-if="quoteState(v.symbol).chart" class="chart-wrap">
                          <svg class="price-chart" :viewBox="'0 0 ' + quoteState(v.symbol).chart.width + ' ' + quoteState(v.symbol).chart.height" preserveAspectRatio="none" role="img">
                            <line
                              v-for="g in quoteState(v.symbol).chart.grid"
                              :key="g.key"
                              :x1="quoteState(v.symbol).chart.plotLeft"
                              :x2="quoteState(v.symbol).chart.width - quoteState(v.symbol).chart.plotRight"
                              :y1="g.y"
                              :y2="g.y"
                              class="chart-grid"
                              vector-effect="non-scaling-stroke"
                            />
                            <polyline
                              :points="quoteState(v.symbol).chart.line"
                              fill="none"
                              :stroke="quoteState(v.symbol).chart.color"
                              stroke-width="2.5"
                              stroke-linejoin="round"
                              stroke-linecap="round"
                              vector-effect="non-scaling-stroke"
                            />
                            <g v-for="m in quoteState(v.symbol).chart.marks" :key="m.key">
                              <circle :cx="m.x" :cy="m.y" r="3.5" :fill="quoteState(v.symbol).chart.color" />
                              <text :x="m.labelX" :y="m.labelY" :text-anchor="m.anchor" class="chart-label">{{ m.label }}</text>
                            </g>
                            <text :x="quoteState(v.symbol).chart.plotLeft" :y="quoteState(v.symbol).chart.height - 7" class="chart-axis">{{ quoteState(v.symbol).chart.firstDate }}</text>
                            <text :x="quoteState(v.symbol).chart.width - quoteState(v.symbol).chart.plotRight" :y="quoteState(v.symbol).chart.height - 7" text-anchor="end" class="chart-axis">{{ quoteState(v.symbol).chart.lastDate }}</text>
                          </svg>
                          <div class="chart-summary">
                            <span>{{ quoteState(v.symbol).chart.count }} 个交易日</span>
                            <strong :class="quoteState(v.symbol).chart.change >= 0 ? 'chg-up' : 'chg-down'">{{ formatSignedPercent(quoteState(v.symbol).chart.change) }}</strong>
                          </div>
                        </div>
                        <div v-else class="chart-message">暂无历史行情</div>
                      </div>
                      <table class="factor-table" v-if="v.detail && v.detail.factors">
                        <thead>
                          <tr><th>因子</th><th>原始值</th><th>得分</th><th>权重</th><th>贡献</th></tr>
                        </thead>
                        <tbody>
                          <tr v-for="f in v.detail.factors" :key="f.key">
                            <td>{{ f.label }}</td>
                            <td>{{ f.value != null ? f.value : '缺失' }}</td>
                            <td>{{ f.score != null ? Number(f.score).toFixed(2) : '-' }}</td>
                            <td>{{ f.weight != null ? Number(f.weight).toFixed(2) : '-' }}</td>
                            <td>{{ f.score != null && f.weight != null ? (Number(f.score) * Number(f.weight)).toFixed(3) : '-' }}</td>
                          </tr>
                        </tbody>
                      </table>
                      <ol class="eval-steps" v-if="v.detail && v.detail.steps">
                        <li v-for="(s, si) in v.detail.steps" :key="si">{{ s }}</li>
                      </ol>
                      <p class="eval-warning" v-if="v.detail && v.detail.warning">提示：{{ v.detail.warning }}</p>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>
      </template>

      <section class="daily-picks">
        <div class="daily-picks-heading">
          <div>
            <h3>每日选股</h3>
            <p>选股结果为规则式筛选,非投资建议</p>
          </div>
          <span v-if="activePickDate">数据日期 {{ activePickDate }}</span>
        </div>

        <div class="pick-tabs" role="tablist" aria-label="每日选股类型">
          <button
            v-for="tab in pickTabs"
            :key="tab.type"
            type="button"
            :class="{ active: activePickType === tab.type }"
            @click="selectPickTab(tab.type)"
          >
            {{ tab.label }}
          </button>
        </div>

        <div v-if="activePickState.loading" class="pick-message">选股数据加载中...</div>
        <div v-else-if="activePickState.error" class="pick-message error-msg">{{ activePickState.error }}</div>
        <div v-else-if="activePickState.picks.length === 0" class="pick-message">暂无(收盘后 15:30/15:40 生成)</div>
        <div v-else class="table-wrapper pick-table-wrapper">
          <table class="stock-table pick-table">
            <thead>
              <tr v-if="activePickType === 'undervalued'">
                <th>排名</th>
                <th>代码</th>
                <th>名称</th>
                <th>市场</th>
                <th>PE</th>
                <th>PB</th>
                <th>评分</th>
              </tr>
              <tr v-else-if="activePickType === 'hot'">
                <th>排名</th>
                <th>代码</th>
                <th>名称</th>
                <th>市场</th>
                <th>热度分</th>
              </tr>
              <tr v-else>
                <th>排名</th>
                <th>代码</th>
                <th>名称</th>
                <th>市场</th>
                <th>PE</th>
                <th>PB</th>
                <th>热度分</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="pick in activePickState.picks">
                <tr :key="pickKey(activePickType, pick)" class="stock-row pick-row" @click="togglePickExpand(activePickType, pick.symbol)">
                  <td>{{ pick.rank || '-' }}</td>
                  <td class="ticker-cell">
                    <button class="ticker-button" type="button" @click.stop="togglePickExpand(activePickType, pick.symbol)">
                      {{ pick.symbol }}
                    </button>
                  </td>
                  <td>{{ pick.name || '-' }}</td>
                  <td>{{ marketLabel(pick.market, pick.symbol) }}</td>
                  <td v-if="activePickType !== 'hot'">{{ formatMetric(pick.metrics && pick.metrics.pe, 2) }}</td>
                  <td v-if="activePickType !== 'hot'">{{ formatMetric(pick.metrics && pick.metrics.pb, 2) }}</td>
                  <td>{{ formatScore(pick.score) }}</td>
                </tr>
                <tr :key="pickKey(activePickType, pick) + '-basis'" class="pick-basis-row">
                  <td :colspan="pickColspan(activePickType)">
                    <div class="pick-basis-content">
                      <p>{{ pick.basis || pickBasisFallback(pick) }}</p>
                      <div v-if="hotEvidenceItems(pick).length" class="pick-evidence">
                        <span v-for="item in hotEvidenceItems(pick)" :key="item" class="evidence-chip">{{ item }}</span>
                      </div>
                      <ul v-if="pickNewsTitles(pick).length" class="pick-news-list">
                        <li v-for="title in pickNewsTitles(pick)" :key="title">{{ title }}</li>
                      </ul>
                    </div>
                  </td>
                </tr>
                <tr v-if="expandedPickKey === pickKey(activePickType, pick)" :key="pickKey(activePickType, pick) + '-chart'" class="detail-row">
                  <td :colspan="pickColspan(activePickType)">
                    <div class="history-section pick-history-section">
                      <div class="section-heading">
                        <h5>{{ pick.name || pick.symbol }} ({{ pick.symbol }}) 历史行情(近一年)</h5>
                        <span v-if="quoteState(pick.symbol).source">来源 {{ quoteSourceLabel(quoteState(pick.symbol).source) }}</span>
                      </div>
                      <div v-if="quoteState(pick.symbol).loading" class="chart-message">历史行情加载中...</div>
                      <div v-else-if="quoteState(pick.symbol).error" class="chart-message error-msg">{{ quoteState(pick.symbol).error }}</div>
                      <div v-else-if="quoteState(pick.symbol).chart" class="chart-wrap">
                        <svg class="price-chart" :viewBox="'0 0 ' + quoteState(pick.symbol).chart.width + ' ' + quoteState(pick.symbol).chart.height" preserveAspectRatio="none" role="img">
                          <line
                            v-for="g in quoteState(pick.symbol).chart.grid"
                            :key="g.key"
                            :x1="quoteState(pick.symbol).chart.plotLeft"
                            :x2="quoteState(pick.symbol).chart.width - quoteState(pick.symbol).chart.plotRight"
                            :y1="g.y"
                            :y2="g.y"
                            class="chart-grid"
                            vector-effect="non-scaling-stroke"
                          />
                          <polyline
                            :points="quoteState(pick.symbol).chart.line"
                            fill="none"
                            :stroke="quoteState(pick.symbol).chart.color"
                            stroke-width="2.5"
                            stroke-linejoin="round"
                            stroke-linecap="round"
                            vector-effect="non-scaling-stroke"
                          />
                          <g v-for="m in quoteState(pick.symbol).chart.marks" :key="m.key">
                            <circle :cx="m.x" :cy="m.y" r="3.5" :fill="quoteState(pick.symbol).chart.color" />
                            <text :x="m.labelX" :y="m.labelY" :text-anchor="m.anchor" class="chart-label">{{ m.label }}</text>
                          </g>
                          <text :x="quoteState(pick.symbol).chart.plotLeft" :y="quoteState(pick.symbol).chart.height - 7" class="chart-axis">{{ quoteState(pick.symbol).chart.firstDate }}</text>
                          <text :x="quoteState(pick.symbol).chart.width - quoteState(pick.symbol).chart.plotRight" :y="quoteState(pick.symbol).chart.height - 7" text-anchor="end" class="chart-axis">{{ quoteState(pick.symbol).chart.lastDate }}</text>
                        </svg>
                        <div class="chart-summary">
                          <span>{{ quoteState(pick.symbol).chart.count }} 个交易日</span>
                          <strong :class="quoteState(pick.symbol).chart.change >= 0 ? 'chg-up' : 'chg-down'">{{ formatSignedPercent(quoteState(pick.symbol).chart.change) }}</strong>
                        </div>
                      </div>
                      <div v-else class="chart-message">暂无历史行情</div>
                    </div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div class="xinwen-panel">
          <div class="xinwen-heading">
            <div>
              <h4>新闻联播要点</h4>
              <p v-if="xinwen.basis">{{ xinwen.basis }}</p>
            </div>
            <div class="xinwen-actions">
              <label>
                <span>日期</span>
                <select
                  v-model="xinwen.selectedDate"
                  :disabled="xinwen.datesLoading || xinwen.loading"
                  @change="loadXinwenLianbo(xinwen.selectedDate, true)"
                >
                  <option v-if="!xinwen.dates.length" value="">最新可用</option>
                  <option v-for="row in xinwen.dates" :key="row.date" :value="row.date">
                    {{ row.date }} ({{ row.total }}条)
                  </option>
                </select>
              </label>
            </div>
          </div>
          <div v-if="xinwen.loading" class="pick-message">新闻联播抓取中...</div>
          <div v-else-if="xinwen.error" class="pick-message error-msg">{{ xinwen.error }}</div>
          <template v-else-if="xinwen.loaded && xinwen.modules.length">
            <div v-if="xinwen.market_hotwords.length" class="hotword-list">
              <span
                v-for="word in xinwen.market_hotwords"
                :key="word.word"
                :title="hotwordTitle(word)"
              >
                {{ word.word }} ×{{ word.count }}
              </span>
            </div>
            <div class="xinwen-module-grid">
              <article v-for="module in xinwen.modules" :key="module.key" class="xinwen-module-card">
                <div class="module-title">
                  <strong>{{ module.label }}</strong>
                  <span>{{ module.count }}条</span>
                </div>
                <p v-if="module.summary" class="module-summary">{{ module.summary }}</p>
                <ul class="module-points" v-if="module.key_points && module.key_points.length">
                  <li v-for="point in module.key_points.slice(0, 3)" :key="point">{{ point }}</li>
                </ul>
                <div class="module-items">
                  <a
                    v-for="item in module.items.slice(0, 3)"
                    :key="item.url || item.title"
                    :href="item.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {{ item.title }}
                  </a>
                </div>
              </article>
            </div>
          </template>
          <div v-else class="pick-message">暂无新闻联播摘要</div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: 'stock-watch',
  data: function () {
    return {
      stocks: [],
      expandedSymbol: '',
      loading: false,
      error: '',
      searchKeyword: '',
      marketFilter: '',
      verdictFilter: '',
      dateFilter: '',
      sortKey: 'symbol',
      sortDirection: 'asc',
      quoteHistories: {},
      pickTabs: [
        { type: 'undervalued', label: '低估 Top10' },
        { type: 'hot', label: '热点 Top10' },
        { type: 'hot_undervalued', label: '热点低估 5' }
      ],
      activePickType: 'undervalued',
      dailyPicks: {
        undervalued: { loading: false, loaded: false, error: '', date: '', total: 0, picks: [] },
        hot: { loading: false, loaded: false, error: '', date: '', total: 0, picks: [] },
        hot_undervalued: { loading: false, loaded: false, error: '', date: '', total: 0, picks: [] }
      },
      xinwen: {
        loading: false,
        loaded: false,
        error: '',
        date: '',
        total: 0,
        basis: '',
        market_hotwords: [],
        modules: [],
        selectedDate: '',
        dates: [],
        datesLoading: false
      },
      expandedPickKey: ''
    }
  },
  computed: {
    modelName: function () {
      var first = this.stocks.find(function (v) { return v.detail && v.detail.model })
      return first ? first.detail.model : ''
    },

    stockDates: function () {
      var dates = {}
      this.stocks.forEach(function (v) {
        if (v.date) dates[v.date] = true
      })
      return Object.keys(dates).sort().reverse()
    },

    filteredStocks: function () {
      var self = this
      var keyword = (this.searchKeyword || '').toUpperCase()
      var rows = this.stocks.filter(function (v) {
        var symbol = (v.symbol || '').toUpperCase()
        var name = (v.name || '').toUpperCase()
        if (keyword && symbol.indexOf(keyword) === -1 && name.indexOf(keyword) === -1) return false
        if (self.marketFilter && self.marketKey(v.symbol) !== self.marketFilter) return false
        if (self.verdictFilter && v.verdict !== self.verdictFilter) return false
        if (self.dateFilter && v.date !== self.dateFilter) return false
        return true
      })

      rows.sort(function (a, b) {
        var av = self.sortValue(a, self.sortKey)
        var bv = self.sortValue(b, self.sortKey)
        var result = 0
        if (av == null && bv != null) result = 1
        else if (av != null && bv == null) result = -1
        else if (typeof av === 'number' && typeof bv === 'number') result = av - bv
        else result = String(av == null ? '' : av).localeCompare(String(bv == null ? '' : bv))
        return self.sortDirection === 'asc' ? result : -result
      })
      return rows
    },

    activePickState: function () {
      return this.dailyPicks[this.activePickType] || { loading: false, loaded: false, error: '', date: '', total: 0, picks: [] }
    },

    activePickDate: function () {
      return this.activePickState.date || ''
    }
  },
  mounted: function () {
    this.loadStocks()
    this.loadDailyPicks(this.activePickType)
    this.loadXinwenDates()
  },
  methods: {
    loadStocks: function () {
      var self = this
      self.loading = true
      self.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/valuations/?group=stock'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.stocks = res.valuations || []
        } else {
          self.error = res.message || '加载失败'
        }
        self.loading = false
      }).catch(function (err) {
        self.error = '请求失败: ' + (err.statusText || err.status || 'unknown')
        self.loading = false
      })
    },

    loadDailyPicks: function (type) {
      var current = this.dailyPicks[type]
      if (!current || current.loading || current.loaded) return

      var self = this
      this.$set(this.dailyPicks, type, {
        loading: true,
        loaded: false,
        error: '',
        date: '',
        total: 0,
        picks: []
      })
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/picks/?type=' + encodeURIComponent(type)
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.$set(self.dailyPicks, type, {
            loading: false,
            loaded: true,
            error: '',
            date: res.date || '',
            total: res.total || 0,
            picks: res.picks || []
          })
        } else {
          self.$set(self.dailyPicks, type, {
            loading: false,
            loaded: true,
            error: res.message || '选股数据加载失败',
            date: res.date || '',
            total: 0,
            picks: []
          })
        }
      }).catch(function (err) {
        self.$set(self.dailyPicks, type, {
          loading: false,
          loaded: true,
          error: '选股请求失败: ' + (err.statusText || err.status || 'unknown'),
          date: '',
          total: 0,
          picks: []
        })
      })
    },

    loadXinwenDates: function () {
      if (this.xinwen.datesLoading) return

      var self = this
      this.xinwen.datesLoading = true
      this.xinwen.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/xinwen-lianbo/dates/?days=10'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.xinwen.dates = res.dates || []
          self.xinwen.selectedDate = self.xinwen.dates.length ? self.xinwen.dates[0].date : ''
        } else {
          self.xinwen.error = res.message || '新闻联播加载失败'
        }
        self.xinwen.datesLoading = false
        self.loadXinwenLianbo(self.xinwen.selectedDate, true)
      }).catch(function (err) {
        self.xinwen.datesLoading = false
        self.xinwen.error = '新闻联播日期请求失败: ' + (err.statusText || err.status || 'unknown')
        self.loadXinwenLianbo('', true)
      })
    },

    loadXinwenLianbo: function (date, force) {
      var selectedDate = date || ''
      if (this.xinwen.loading) return
      if (!force && this.xinwen.loaded && this.xinwen.date === selectedDate) return

      var self = this
      this.xinwen.loading = true
      this.xinwen.loaded = false
      this.xinwen.error = ''
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/xinwen-lianbo/'
      if (selectedDate) reqUrl += '?date=' + encodeURIComponent(selectedDate)
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          self.xinwen.loading = false
          self.xinwen.loaded = true
          self.xinwen.error = ''
          self.xinwen.date = res.date || selectedDate
          self.xinwen.selectedDate = res.date || selectedDate
          self.xinwen.total = res.total || 0
          self.xinwen.basis = res.basis || ''
          self.xinwen.market_hotwords = res.market_hotwords || []
          self.xinwen.modules = res.modules || []
        } else {
          self.xinwen.loading = false
          self.xinwen.loaded = true
          self.xinwen.error = res.message || '新闻联播加载失败'
        }
      }).catch(function (err) {
        self.xinwen.loading = false
        self.xinwen.loaded = true
        self.xinwen.error = '新闻联播请求失败: ' + (err.statusText || err.status || 'unknown')
      })
    },

    selectPickTab: function (type) {
      this.activePickType = type
      this.loadDailyPicks(type)
      if (this.expandedPickKey && this.expandedPickKey.indexOf(type + ':') !== 0) {
        this.expandedPickKey = ''
      }
    },

    resetFilters: function () {
      this.searchKeyword = ''
      this.marketFilter = ''
      this.verdictFilter = ''
      this.dateFilter = ''
      this.sortKey = 'symbol'
      this.sortDirection = 'asc'
    },

    sortBy: function (key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        this.sortKey = key
        this.sortDirection = key === 'symbol' || key === 'name' || key === 'market' ? 'asc' : 'desc'
      }
    },

    sortIndicator: function (key) {
      if (this.sortKey !== key) return ''
      return this.sortDirection === 'asc' ? '↑' : '↓'
    },

    sortValue: function (row, key) {
      if (key === 'market') return this.marketOf(row.symbol)
      if (key === 'composite') return this.compositeNumber(row)
      if (key === 'date') return row.date
      return row[key]
    },

    marketKey: function (symbol) {
      if (/\.(SS|SZ|BJ)$/.test(symbol || '')) return 'ashare'
      if (/\.HK$/.test(symbol || '')) return 'hk'
      return 'us'
    },

    marketOf: function (symbol) {
      var key = this.marketKey(symbol)
      if (key === 'ashare') return 'A股'
      if (key === 'hk') return '港股'
      return '美股'
    },

    marketLabel: function (market, symbol) {
      if (market === 'ashare') return 'A股'
      if (market === 'hk') return '港股'
      if (market === 'us') return '美股'
      return market || this.marketOf(symbol)
    },

    marketCount: function (key) {
      var self = this
      return this.stocks.filter(function (v) { return self.marketKey(v.symbol) === key }).length
    },

    changeClass: function (pct) {
      if (pct == null) return ''
      return Number(pct) > 0 ? 'chg-up' : (Number(pct) < 0 ? 'chg-down' : '')
    },

    toggleExpand: function (symbol) {
      if (this.expandedSymbol === symbol) {
        this.expandedSymbol = ''
      } else {
        this.expandedSymbol = symbol
        this.loadQuoteHistory(symbol)
      }
    },

    togglePickExpand: function (type, symbol) {
      var key = type + ':' + symbol
      if (this.expandedPickKey === key) {
        this.expandedPickKey = ''
      } else {
        this.expandedPickKey = key
        this.loadQuoteHistory(symbol)
      }
    },

    quoteState: function (symbol) {
      return this.quoteHistories[symbol] || {
        loading: false,
        loaded: false,
        error: '',
        quotes: [],
        source: '',
        chart: null
      }
    },

    quoteSourceLabel: function (source) {
      return source === 'db' ? '本地库' : (source === 'tencent' ? '腾讯' : source)
    },

    loadQuoteHistory: function (symbol) {
      var existing = this.quoteHistories[symbol]
      if (existing && (existing.loading || existing.loaded)) return

      var self = this
      this.$set(this.quoteHistories, symbol, {
        loading: true,
        loaded: false,
        error: '',
        quotes: [],
        source: '',
        chart: null
      })
      var reqUrl = this.$store.state.baseUrl + '/blog/api/market/quotes/' + encodeURIComponent(symbol) + '/?days=250'
      this.$http.get(reqUrl).then(function (response) {
        var res = JSON.parse(response.bodyText)
        if (res.result === 'success') {
          var quotes = res.quotes || []
          self.$set(self.quoteHistories, symbol, {
            loading: false,
            loaded: true,
            error: '',
            quotes: quotes,
            source: res.source || '',
            chart: self.buildQuoteChart(quotes)
          })
        } else {
          self.$set(self.quoteHistories, symbol, {
            loading: false,
            loaded: true,
            error: res.message || '历史行情加载失败',
            quotes: [],
            source: '',
            chart: null
          })
        }
      }).catch(function (err) {
        self.$set(self.quoteHistories, symbol, {
          loading: false,
          loaded: true,
          error: '历史行情请求失败: ' + (err.statusText || err.status || 'unknown'),
          quotes: [],
          source: '',
          chart: null
        })
      })
    },

    buildQuoteChart: function (quotes) {
      var points = (quotes || []).map(function (q) {
        return {
          date: q.date,
          close: Number(q.close)
        }
      }).filter(function (q) {
        return q.date && !isNaN(q.close) && q.close > 0
      })
      if (points.length < 2) return null

      var width = 720
      var height = 160
      var plotLeft = 46
      var plotRight = 18
      var plotTop = 18
      var plotBottom = 30
      var min = Math.min.apply(null, points.map(function (p) { return p.close }))
      var max = Math.max.apply(null, points.map(function (p) { return p.close }))
      var range = max - min
      if (range === 0) range = Math.max(max * 0.02, 1)
      var plotWidth = width - plotLeft - plotRight
      var plotHeight = height - plotTop - plotBottom

      var mapped = points.map(function (p, i) {
        var x = plotLeft + (points.length === 1 ? 0 : (plotWidth * i / (points.length - 1)))
        var y = plotTop + ((max - p.close) / range * plotHeight)
        return {
          x: Math.round(x * 10) / 10,
          y: Math.round(y * 10) / 10,
          date: p.date,
          close: p.close
        }
      })
      var first = mapped[0]
      var last = mapped[mapped.length - 1]
      var high = mapped.reduce(function (a, b) { return a.close >= b.close ? a : b })
      var low = mapped.reduce(function (a, b) { return a.close <= b.close ? a : b })
      var firstClose = first.close
      var lastClose = last.close
      var change = firstClose ? ((lastClose - firstClose) / firstClose * 100) : 0
      var self = this

      function mark (key, point, label, anchor, dx, dy) {
        return {
          key: key,
          x: point.x,
          y: point.y,
          label: label + ' ' + self.formatPrice(point.close, 2),
          labelX: point.x + dx,
          labelY: Math.max(12, Math.min(height - 34, point.y + dy)),
          anchor: anchor
        }
      }

      return {
        width: width,
        height: height,
        plotLeft: plotLeft,
        plotRight: plotRight,
        line: mapped.map(function (p) { return p.x + ',' + p.y }).join(' '),
        color: lastClose >= firstClose ? '#c62828' : '#2e7d32',
        firstDate: first.date,
        lastDate: last.date,
        count: mapped.length,
        change: change,
        grid: [
          { key: 'top', y: plotTop },
          { key: 'middle', y: plotTop + plotHeight / 2 },
          { key: 'bottom', y: plotTop + plotHeight }
        ],
        marks: [
          mark('first', first, '首', 'start', 8, first.y > plotTop + plotHeight / 2 ? -8 : 16),
          mark('last', last, '尾', 'end', -8, last.y > plotTop + plotHeight / 2 ? -8 : 16),
          mark('high', high, '高', high.x > width - 160 ? 'end' : 'start', high.x > width - 160 ? -8 : 8, -8),
          mark('low', low, '低', low.x > width - 160 ? 'end' : 'start', low.x > width - 160 ? -8 : 8, 16)
        ]
      }
    },

    compositeNumber: function (v) {
      if (!v.detail || v.detail.composite == null) return null
      var num = Number(v.detail.composite)
      return isNaN(num) ? null : num
    },

    compositeOf: function (v) {
      var c = this.compositeNumber(v)
      if (c == null) return '-'
      return (c > 0 ? '+' : '') + c.toFixed(2)
    },

    compositeClass: function (v) {
      var c = this.compositeNumber(v)
      if (c == null) return ''
      return Math.abs(c) >= (v.detail.threshold || 1.0) ? 'composite-active' : ''
    },

    verdictLabel: function (verdict) {
      return { undervalued: '低估', overvalued: '高估', neutral: '中性' }[verdict] || verdict || '-'
    },

    formatPrice: function (val, digits) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      return num.toFixed(digits == null ? 2 : digits)
    },

    formatPercent: function (val, digits, scale) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      if (scale !== false) num = num * 100
      return num.toFixed(digits == null ? 2 : digits) + '%'
    },

    formatSignedPercent: function (val) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      return (num > 0 ? '+' : '') + num.toFixed(2) + '%'
    },

    formatMetric: function (val, digits) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      return num.toFixed(digits == null ? 2 : digits)
    },

    formatScore: function (val) {
      if (val == null || val === '') return '-'
      var num = Number(val)
      if (isNaN(num)) return '-'
      return num.toFixed(3)
    },

    pickKey: function (type, pick) {
      return type + ':' + (pick.symbol || '')
    },

    pickColspan: function (type) {
      return type === 'hot' ? 5 : 7
    },

    pickBasisFallback: function (pick) {
      var metrics = pick.metrics || {}
      var parts = []
      if (metrics.hot_rank != null) parts.push('人气榜第' + metrics.hot_rank + '名')
      if (metrics.news_hits) parts.push('24h要闻命中' + metrics.news_hits + '次')
      if (metrics.news_titles && metrics.news_titles.length) parts.push('标题: ' + metrics.news_titles[0])
      return parts.join('; ') || '-'
    },

    pickNewsTitles: function (pick) {
      var metrics = pick.metrics || {}
      return (metrics.news_titles || []).slice(0, 3)
    },

    hotEvidenceItems: function (pick) {
      var metrics = pick.metrics || {}
      var items = []
      if (metrics.hot_rank != null) items.push('东财人气榜 #' + metrics.hot_rank)
      if (metrics.news_hits != null) items.push('24h财经要闻命中 ' + metrics.news_hits + ' 次')
      if (metrics.hot_score != null || pick.score != null) items.push('热度评分: 60%人气榜 + 40%新闻命中')
      if (metrics.hot_score != null) items.push('热度分 ' + this.formatScore(metrics.hot_score))
      return items
    },

    hotwordTitle: function (word) {
      var evidence = word && word.evidence ? word.evidence : []
      return evidence.length ? ('依据: ' + evidence.join(' / ')) : ''
    }
  }
}
</script>

<style scoped>
.stock-watch {
  min-height: 100vh;
}

#blog-header {
  padding: 3rem 1rem 2rem;
  text-align: center;
}

.page-title {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

.page-desc {
  color: #888;
}

.stock-content {
  max-width: 1180px;
  margin: 0 auto;
  padding: 0 1rem 3rem;
}

.stock-toolbar {
  display: flex;
  gap: 14px;
  align-items: center;
  margin-bottom: 14px;
  flex-wrap: wrap;
}

.stock-toolbar button,
.sort-dir,
.reset-btn {
  border: 1px solid #d2d7df;
  background: #fff;
  color: #263238;
  border-radius: 4px;
  cursor: pointer;
  padding: 7px 14px;
}

.stock-toolbar button:disabled {
  cursor: wait;
  color: #999;
}

.manage-link {
  font-size: 14px;
  color: #3273dc;
  text-decoration: none;
}

.manage-link:hover {
  text-decoration: underline;
}

.model-name {
  font-size: 13px;
  color: #888;
  font-family: monospace;
  margin-left: auto;
}

.stats-row {
  display: grid;
  grid-template-columns: repeat(5, minmax(110px, 1fr));
  gap: 10px;
  margin: 10px 0 14px;
}

.stat-item {
  border: 1px solid #e6e9ef;
  border-radius: 6px;
  padding: 10px 12px;
  background: #fff;
}

.stat-item span {
  display: block;
  color: #777;
  font-size: 12px;
  margin-bottom: 4px;
}

.stat-item strong {
  color: #222;
  font-size: 20px;
}

.filter-panel {
  display: grid;
  grid-template-columns: minmax(180px, 1.4fr) repeat(4, minmax(120px, 1fr)) auto auto;
  gap: 10px;
  align-items: end;
  margin-bottom: 12px;
  padding: 12px;
  border: 1px solid #e5e8ee;
  border-radius: 6px;
  background: #fafbfc;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.filter-field span {
  color: #777;
  font-size: 12px;
}

.filter-field input,
.filter-field select {
  width: 100%;
  border: 1px solid #d2d7df;
  border-radius: 4px;
  padding: 7px 9px;
  background: #fff;
  font-size: 13px;
  box-sizing: border-box;
}

.model-hint {
  font-size: 12px;
  color: #888;
  margin: 6px 0 10px;
}

.loading,
.error-msg,
.empty-hint {
  padding: 1rem 0;
}

.error-msg {
  color: #c62828;
}

.empty-hint {
  color: #888;
  font-size: 14px;
}

.empty-hint a {
  color: #3273dc;
}

.table-wrapper {
  overflow-x: auto;
  border: 1px solid #edf0f4;
  border-radius: 6px;
}

.stock-table {
  width: 100%;
  min-width: 930px;
  border-collapse: collapse;
  font-size: 13px;
  background: #fff;
}

.stock-table th,
.stock-table td {
  border-bottom: 1px solid #eee;
  padding: 8px 10px;
  text-align: left;
  vertical-align: middle;
}

.stock-table th {
  background: #f6f8fb;
  color: #555;
  font-weight: 600;
  white-space: nowrap;
}

.sort-button {
  border: none;
  background: transparent;
  padding: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
}

.ticker-cell {
  font-family: monospace;
  font-weight: bold;
}

.stock-row {
  cursor: pointer;
}

.stock-row:hover {
  background: #f5f8ff;
}

.chg-up {
  color: #c62828;
}

.chg-down {
  color: #2e7d32;
}

.composite-active {
  font-weight: bold;
}

.confidence {
  font-size: 11px;
  color: #999;
  margin-left: 4px;
}

.verdict-undervalued {
  color: #2e7d32;
  font-weight: bold;
}

.verdict-overvalued {
  color: #c62828;
  font-weight: bold;
}

.verdict-neutral {
  color: #777;
}

.detail-row td {
  background: #fbfcff;
  padding: 14px 16px;
}

.eval-process h4 {
  margin: 0;
  font-size: 15px;
}

.detail-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  margin-bottom: 12px;
}

.detail-heading span {
  color: #888;
  font-size: 12px;
}

.detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 8px;
  margin-bottom: 12px;
}

.detail-grid div {
  border: 1px solid #e4e9f1;
  border-radius: 4px;
  padding: 8px 10px;
  background: #fff;
}

.detail-grid span {
  display: block;
  color: #777;
  font-size: 12px;
  margin-bottom: 3px;
}

.detail-grid strong {
  color: #222;
  font-size: 14px;
}

.history-section {
  border: 1px solid #e4e9f1;
  border-radius: 4px;
  background: #fff;
  padding: 10px 12px;
  margin-bottom: 12px;
}

.section-heading {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.section-heading h5 {
  margin: 0;
  font-size: 13px;
  color: #333;
}

.section-heading span {
  color: #888;
  font-size: 12px;
}

.chart-wrap {
  width: 100%;
}

.price-chart {
  display: block;
  width: 100%;
  height: 160px;
}

.chart-grid {
  stroke: #edf0f4;
  stroke-width: 1;
}

.chart-label {
  fill: #444;
  font-size: 11px;
}

.chart-axis {
  fill: #888;
  font-size: 11px;
}

.chart-summary {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  align-items: center;
  color: #777;
  font-size: 12px;
  margin-top: 2px;
}

.chart-message {
  color: #888;
  font-size: 12px;
  padding: 24px 0;
  text-align: center;
}

.factor-table {
  width: auto;
  min-width: 60%;
  border-collapse: collapse;
  font-size: 12px;
  margin-bottom: 10px;
}

.factor-table th,
.factor-table td {
  border: 1px solid #e0e8f0;
  padding: 5px 10px;
  text-align: right;
}

.factor-table th:first-child,
.factor-table td:first-child {
  text-align: left;
}

.eval-steps {
  font-size: 12px;
  color: #555;
  line-height: 1.8;
  padding-left: 18px;
  margin: 0;
}

.eval-warning {
  color: #c62828;
  font-size: 12px;
  margin: 8px 0 0;
}

.daily-picks {
  border: 1px solid #e5e8ee;
  border-radius: 6px;
  background: #fff;
  margin-top: 18px;
  padding: 14px;
}

.daily-picks-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 12px;
}

.daily-picks-heading h3 {
  margin: 0 0 4px;
  font-size: 17px;
  color: #222;
}

.daily-picks-heading p,
.daily-picks-heading span {
  margin: 0;
  color: #888;
  font-size: 12px;
}

.pick-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.pick-tabs button {
  border: 1px solid #d2d7df;
  background: #fafbfc;
  color: #555;
  border-radius: 4px;
  cursor: pointer;
  padding: 7px 12px;
  font-size: 13px;
}

.pick-tabs button.active {
  background: #263238;
  border-color: #263238;
  color: #fff;
}

.pick-message {
  color: #888;
  font-size: 13px;
  padding: 18px 0;
  text-align: center;
}

.pick-table-wrapper {
  border-color: #edf0f4;
}

.pick-table {
  min-width: 760px;
}

.pick-row td {
  border-bottom: none;
}

.ticker-button {
  border: none;
  background: transparent;
  color: #3273dc;
  cursor: pointer;
  font: inherit;
  font-weight: bold;
  padding: 0;
}

.ticker-button:hover {
  text-decoration: underline;
}

.pick-basis-row td {
  color: #888;
  font-size: 12px;
  line-height: 1.6;
  padding-top: 0;
}

.pick-basis-content p {
  margin: 0 0 6px;
}

.pick-evidence {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 6px;
}

.evidence-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #e4e9f1;
  border-radius: 999px;
  background: #f7f9fc;
  color: #455a64;
  line-height: 1;
  padding: 5px 8px;
}

.pick-news-list {
  margin: 0;
  padding-left: 16px;
  color: #666;
}

.pick-history-section {
  margin-bottom: 0;
}

.xinwen-panel {
  border-top: 1px solid #edf0f4;
  margin-top: 14px;
  padding-top: 14px;
}

.xinwen-heading {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: flex-start;
  margin-bottom: 10px;
}

.xinwen-heading h4 {
  margin: 0 0 4px;
  color: #222;
  font-size: 15px;
}

.xinwen-heading p,
.xinwen-heading span {
  margin: 0;
  color: #888;
  font-size: 12px;
  line-height: 1.5;
}

.xinwen-actions {
  display: flex;
  justify-content: flex-end;
  min-width: 180px;
}

.xinwen-actions label {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #777;
  font-size: 12px;
}

.xinwen-actions select {
  border: 1px solid #d2d7df;
  border-radius: 4px;
  background: #fff;
  color: #263238;
  font-size: 13px;
  height: 32px;
  padding: 0 8px;
}

.hotword-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 12px;
}

.hotword-list span {
  border: 1px solid #ffd8b5;
  border-radius: 999px;
  background: #fff8f0;
  color: #9a4b00;
  font-size: 12px;
  line-height: 1;
  padding: 6px 9px;
}

.xinwen-module-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 10px;
}

.xinwen-module-card {
  border: 1px solid #e5e8ee;
  border-radius: 6px;
  background: #fbfcff;
  padding: 10px 12px;
}

.module-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 8px;
}

.module-title strong {
  color: #263238;
  font-size: 14px;
}

.module-title span {
  color: #888;
  font-size: 12px;
}

.module-summary {
  color: #555;
  font-size: 12px;
  line-height: 1.7;
  margin: 0 0 8px;
}

.module-points {
  margin: 0 0 8px;
  padding-left: 16px;
  color: #555;
  font-size: 12px;
  line-height: 1.7;
}

.module-items {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.module-items a {
  color: #3273dc;
  font-size: 12px;
  line-height: 1.5;
  text-decoration: none;
}

.module-items a:hover {
  text-decoration: underline;
}

@media (max-width: 900px) {
  .stats-row {
    grid-template-columns: repeat(2, minmax(120px, 1fr));
  }

  .filter-panel {
    grid-template-columns: 1fr 1fr;
  }

  .search-field {
    grid-column: 1 / -1;
  }

  .model-name {
    margin-left: 0;
    width: 100%;
  }
}

@media (max-width: 560px) {
  .filter-panel,
  .stats-row {
    grid-template-columns: 1fr;
  }

  .detail-heading {
    align-items: flex-start;
    flex-direction: column;
  }

  .section-heading,
  .daily-picks-heading,
  .xinwen-heading,
  .chart-summary {
    align-items: flex-start;
    flex-direction: column;
  }

  .xinwen-actions {
    justify-content: flex-start;
    width: 100%;
  }
}
</style>
